import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button.js';

// ── Types ─────────────────────────────────────────────────────────────────────

export type DatePickerSize = 'sm' | 'md' | 'lg';

interface SingleProps {
  mode: 'single';
  value: Date | null;
  onChange: (value: Date | null) => void;
}

interface RangeProps {
  mode: 'range';
  value: { from: Date | null; to: Date | null };
  onChange: (value: { from: Date | null; to: Date | null }) => void;
}

type ModeProps = SingleProps | RangeProps;

interface CommonProps {
  placeholder?: string;
  size?: DatePickerSize;
  label?: string;
  helperText?: string;
  error?: boolean | string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  fullWidth?: boolean;
  format?: (d: Date) => string;
}

export type DatePickerProps = ModeProps & CommonProps;

// ── Sizing (mirrors Input) ────────────────────────────────────────────────────

const H:  Record<DatePickerSize, string> = { sm: '28px', md: '32px', lg: '40px' };
const FS: Record<DatePickerSize, string> = { sm: '12px', md: '13px', lg: '14px' };
const PX: Record<DatePickerSize, string> = { sm: '8px',  md: '10px', lg: '12px' };

const LABEL_STYLE = {
  display: 'block' as const,
  marginBottom: '4px',
  fontSize: '11px',
  fontWeight: 500,
  color: 'var(--evo-fg-3)',
  fontFamily: 'var(--evo-font)',
} as const;

const HELPER_STYLE = {
  display: 'block' as const,
  marginTop: '4px',
  fontSize: '11px',
  fontFamily: 'var(--evo-font)',
} as const;

// ── Locale ────────────────────────────────────────────────────────────────────

const DAYS   = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// ── Date helpers ──────────────────────────────────────────────────────────────

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function defaultFormat(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// Build 42-cell grid starting from the Monday of the week containing the 1st
function buildGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  // getDay(): 0=Sun, so convert to Mon-based: (0→6, 1→0, 2→1, ... 6→5)
  const dow = (first.getDay() + 6) % 7;
  const grid: Date[] = [];
  for (let i = -dow; i < 42 - dow; i++) {
    grid.push(new Date(year, month, 1 + i));
  }
  return grid;
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function CalendarIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function ChevronIcon({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {dir === 'left'
        ? <polyline points="15 18 9 12 15 6" />
        : <polyline points="9 18 15 12 9 6" />}
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ── Calendar grid ─────────────────────────────────────────────────────────────

interface CalendarProps {
  year: number;
  month: number;
  onNav: (y: number, m: number) => void;
  mode: 'single' | 'range';
  singleValue: Date | null;
  rangeFrom: Date | null;
  rangeTo: Date | null;
  hoverDate: Date | null;
  onHover: (d: Date | null) => void;
  onSelect: (d: Date) => void;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
}

function Calendar({
  year, month, onNav, mode,
  singleValue, rangeFrom, rangeTo, hoverDate,
  onHover, onSelect, minDate, maxDate,
}: CalendarProps) {
  const today = startOfDay(new Date());
  const grid  = buildGrid(year, month);

  function isDisabled(d: Date): boolean {
    if (minDate && d < startOfDay(minDate)) return true;
    if (maxDate && d > startOfDay(maxDate)) return true;
    return false;
  }

  function isInRange(d: Date): boolean {
    if (mode !== 'range') return false;
    const from = rangeFrom ? startOfDay(rangeFrom) : null;
    const to   = (rangeTo ?? hoverDate) ? startOfDay(rangeTo ?? hoverDate!) : null;
    if (!from || !to) return false;
    const [lo, hi] = from <= to ? [from, to] : [to, from];
    return d > lo && d < hi;
  }

  function isRangeEdge(d: Date): boolean {
    if (mode !== 'range') return false;
    if (rangeFrom && isSameDay(d, rangeFrom)) return true;
    if (rangeTo   && isSameDay(d, rangeTo))   return true;
    return false;
  }

  function isSelected(d: Date): boolean {
    if (mode === 'single') return singleValue != null && isSameDay(d, singleValue);
    return isRangeEdge(d);
  }

  function prevMonth() {
    if (month === 0) onNav(year - 1, 11);
    else onNav(year, month - 1);
  }
  function nextMonth() {
    if (month === 11) onNav(year + 1, 0);
    else onNav(year, month + 1);
  }

  return (
    <div style={{ padding: '12px', minWidth: 268 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button
          type="button"
          onClick={prevMonth}
          style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--evo-fg-3)', padding: 4, borderRadius: 4 }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--evo-fg-1)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--evo-fg-3)'; }}
        >
          <ChevronIcon dir="left" />
        </button>
        <span style={{ fontSize: '13px', fontWeight: 600, fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-1)' }}>
          {MONTHS[month]} {year}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--evo-fg-3)', padding: 4, borderRadius: 4 }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--evo-fg-1)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--evo-fg-3)'; }}
        >
          <ChevronIcon dir="right" />
        </button>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
        {DAYS.map(d => (
          <div key={d} style={{
            textAlign: 'center', fontSize: '10px', fontWeight: 500,
            textTransform: 'uppercase', color: 'var(--evo-fg-4)',
            fontFamily: 'var(--evo-font)', padding: '2px 0',
          }}>
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px 0' }}>
        {grid.map((d, idx) => {
          const outside  = d.getMonth() !== month;
          const disabled = isDisabled(d);
          const selected = isSelected(d);
          const inRange  = isInRange(d);
          const todayDay = isSameDay(d, today);

          return (
            <div
              key={idx}
              onMouseEnter={() => !disabled && onHover(d)}
              onMouseLeave={() => onHover(null)}
              onClick={() => !disabled && !outside && onSelect(d)}
              style={{
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontFamily: 'var(--evo-font)',
                borderRadius: 'var(--evo-r)',
                cursor: disabled || outside ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.3 : outside ? 0.4 : 1,
                color: selected
                  ? 'var(--evo-fg-on-cyan)'
                  : inRange
                    ? 'var(--evo-fg-1)'
                    : 'var(--evo-fg-2)',
                background: selected
                  ? 'var(--evo-cyan)'
                  : inRange
                    ? 'var(--evo-cyan-12)'
                    : 'transparent',
                border: todayDay && !selected ? '1px solid var(--evo-cyan)' : '1px solid transparent',
                transition: 'background 80ms',
              }}
              onMouseOver={e => {
                if (!disabled && !outside && !selected && !inRange) {
                  (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-bg-3)';
                }
              }}
              onMouseOut={e => {
                if (!selected && !inRange) {
                  (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                }
              }}
            >
              {d.getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── DatePicker ────────────────────────────────────────────────────────────────

type MenuPos = { top: number; left: number };

export function DatePicker(props: DatePickerProps) {
  const {
    placeholder,
    size = 'md',
    label,
    helperText,
    error,
    minDate,
    maxDate,
    disabled = false,
    fullWidth = false,
    format = defaultFormat,
  } = props;

  const today    = new Date();
  const [open, setOpen]         = useState(false);
  const [focused, setFocused]   = useState(false);
  const [navYear, setNavYear]   = useState(today.getFullYear());
  const [navMonth, setNavMonth] = useState(today.getMonth());
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  // range: track first click before second
  const [pendingFrom, setPendingFrom] = useState<Date | null>(null);
  const [menuPos, setMenuPos]   = useState<MenuPos | null>(null);

  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef    = useRef<HTMLDivElement>(null);

  const hasError = !!error;
  const errorMsg = typeof error === 'string' ? error : undefined;
  const isSingle = props.mode === 'single';

  // ── Display text ─────────────────────────────────────────────────────────

  const defaultPlaceholder = isSingle ? 'Pick a date' : 'Pick a range';

  const displayText = (() => {
    if (isSingle) {
      return props.value ? format(props.value) : null;
    } else {
      const { from, to } = props.value;
      if (!from && !to) return null;
      if (from && to)   return `${format(from)} → ${format(to)}`;
      if (from)         return `${format(from)} → …`;
      return null;
    }
  })();

  // ── Position ──────────────────────────────────────────────────────────────

  function computePos(): MenuPos | null {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    return { top: rect.bottom + 4, left: rect.left };
  }

  useLayoutEffect(() => {
    if (!open) { setMenuPos(null); return; }
    setMenuPos(computePos());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    function reposition() { setMenuPos(computePos()); }
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Click outside ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !menuRef.current?.contains(t)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // ── Escape ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    function handle(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open]);

  // ── Day select ────────────────────────────────────────────────────────────

  function handleDaySelect(d: Date) {
    if (isSingle) {
      props.onChange(d);
      setOpen(false);
    } else {
      if (!pendingFrom) {
        setPendingFrom(d);
        props.onChange({ from: d, to: null });
      } else {
        const [from, to] = d < pendingFrom ? [d, pendingFrom] : [pendingFrom, d];
        props.onChange({ from, to });
        setPendingFrom(null);
        setOpen(false);
      }
    }
  }

  function handleClear(e: React.MouseEvent) {
    e.stopPropagation();
    if (isSingle) props.onChange(null);
    else { props.onChange({ from: null, to: null }); setPendingFrom(null); }
  }

  function handleToday() {
    const t = startOfDay(new Date());
    if (isSingle) { props.onChange(t); setOpen(false); }
    else          { props.onChange({ from: t, to: t }); setPendingFrom(null); setOpen(false); }
  }

  function handleClearBtn() {
    if (isSingle) { props.onChange(null); }
    else          { props.onChange({ from: null, to: null }); setPendingFrom(null); }
  }

  const singleValue = isSingle ? props.value : null;
  const rangeFrom   = !isSingle ? props.value.from : null;
  const rangeTo     = !isSingle ? props.value.to : null;
  const hasValue    = isSingle ? props.value != null : (props.value.from != null || props.value.to != null);

  const borderColor = hasError
    ? 'var(--evo-danger)'
    : (open || focused) ? 'var(--evo-cyan)'
    : 'var(--evo-stroke)';
  const shadow = (open || focused)
    ? `0 0 0 1px ${hasError ? 'var(--evo-danger)' : 'var(--evo-cyan)'}`
    : 'none';

  // ── Popover ───────────────────────────────────────────────────────────────

  const popover = open && menuPos ? createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: menuPos.top,
        left: menuPos.left,
        background: 'var(--evo-bg-2)',
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      <Calendar
        year={navYear} month={navMonth}
        onNav={(y, m) => { setNavYear(y); setNavMonth(m); }}
        mode={props.mode}
        singleValue={singleValue}
        rangeFrom={pendingFrom ?? rangeFrom}
        rangeTo={rangeTo}
        hoverDate={hoverDate}
        onHover={setHoverDate}
        onSelect={handleDaySelect}
        minDate={minDate}
        maxDate={maxDate}
      />
      {/* Footer */}
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: 6,
        padding: '8px 12px', borderTop: '1px solid var(--evo-stroke)',
      }}>
        <Button variant="ghost" tone="muted" size="sm" onClick={handleClearBtn}>
          Limpiar
        </Button>
        <Button variant="ghost" tone="cyan" size="sm" onClick={handleToday}>
          Hoy
        </Button>
      </div>
    </div>,
    document.body,
  ) : null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: fullWidth ? 'block' : 'inline-block', width: fullWidth ? '100%' : 'auto' }}>
      {label != null && <label style={LABEL_STYLE}>{label}</label>}
      <div
        ref={triggerRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={() => { if (!disabled) setOpen(o => !o); }}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!disabled) setOpen(o => !o); } }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          height: H[size],
          width: fullWidth ? '100%' : 'auto',
          paddingLeft: PX[size],
          paddingRight: PX[size],
          background: 'var(--evo-bg-3)',
          border: `1px solid ${borderColor}`,
          borderRadius: 'var(--evo-r)',
          boxShadow: shadow,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'border-color 120ms linear, box-shadow 120ms linear',
          boxSizing: 'border-box',
          outline: 'none',
          userSelect: 'none',
          minWidth: 160,
        }}
      >
        <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-fg-3)', lineHeight: 0 }}>
          <CalendarIcon />
        </span>
        <span style={{
          flex: 1,
          fontSize: FS[size],
          fontFamily: 'var(--evo-font)',
          color: displayText ? 'var(--evo-fg-2)' : 'var(--evo-fg-4)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1,
        }}>
          {displayText ?? (placeholder ?? defaultPlaceholder)}
        </span>
        {hasValue && (
          <span
            role="button"
            aria-label="Clear"
            onMouseDown={handleClear}
            style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--evo-fg-3)', lineHeight: 0, padding: 2, borderRadius: 3 }}
            onMouseEnter={e => { (e.currentTarget as HTMLSpanElement).style.color = 'var(--evo-fg-2)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLSpanElement).style.color = 'var(--evo-fg-3)'; }}
          >
            <CloseIcon />
          </span>
        )}
      </div>
      {(helperText != null || errorMsg != null) && (
        <span style={{ ...HELPER_STYLE, color: errorMsg != null ? 'var(--evo-danger)' : 'var(--evo-fg-3)' }}>
          {errorMsg ?? helperText}
        </span>
      )}
      {popover}
    </div>
  );
}
